import { useEffect, useState } from "react";
import { getProfile, updateProfile } from "../../api/accountApi";
import { useAuth } from "../../auth/useAuth";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";

const ProfilePage = () => {
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    displayName: "",
    firstName: "",
    lastName: "",
    avatarUrl: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        const body = await getProfile();
        if (!cancelled) {
          setProfile(body.profile);
          setForm({
            displayName: body.profile.displayName || "",
            firstName: body.profile.firstName || "",
            lastName: body.profile.lastName || "",
            avatarUrl: body.profile.avatarUrl || "",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error.message || "Unable to load profile.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const body = await updateProfile({
        displayName: form.displayName.trim() || null,
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        avatarUrl: form.avatarUrl.trim() || null,
      });
      setProfile(body.profile);
      setSuccessMessage("Profile updated.");
      await refreshUser();
    } catch (error) {
      const errors = error.body?.errors;
      if (Array.isArray(errors) && errors.length) {
        setErrorMessage(errors.join(" "));
      } else {
        setErrorMessage(error.message || "Unable to save profile.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="muted panel-card">Loading profile…</p>;
  }

  return (
    <>
      {successMessage ? (
        <section className="panel-card account-security-flash">
          <p>{successMessage}</p>
        </section>
      ) : null}

      {errorMessage ? (
        <section className="panel-card">
          <p className="admin-data-health-error">{errorMessage}</p>
        </section>
      ) : null}

      <form className="panel-card account-profile-form admin-users-form" onSubmit={handleSubmit}>
        <h2>Profile</h2>
        <p className="muted admin-users-help">
          Your email and access level are managed by administrators and cannot be changed here.
        </p>

        <label className="admin-users-field">
          <span>Email</span>
          <input type="email" value={profile?.email || ""} readOnly disabled />
        </label>

        <p className="muted account-profile-meta">
          Email verified: {profile?.emailVerified ? "Yes" : "No"}
          {profile?.lastLoginAt ? ` · Last sign-in: ${formatDateTime(profile.lastLoginAt)}` : ""}
        </p>

        <label className="admin-users-field">
          <span>Display name</span>
          <input
            type="text"
            value={form.displayName}
            onChange={(event) => setForm((f) => ({ ...f, displayName: event.target.value }))}
            maxLength={255}
          />
        </label>

        <div className="admin-users-form__split">
          <label className="admin-users-field">
            <span>First name</span>
            <input
              type="text"
              value={form.firstName}
              onChange={(event) => setForm((f) => ({ ...f, firstName: event.target.value }))}
              maxLength={150}
            />
          </label>
          <label className="admin-users-field">
            <span>Last name</span>
            <input
              type="text"
              value={form.lastName}
              onChange={(event) => setForm((f) => ({ ...f, lastName: event.target.value }))}
              maxLength={150}
            />
          </label>
        </div>

        <label className="admin-users-field">
          <span>Avatar URL</span>
          <input
            type="url"
            value={form.avatarUrl}
            onChange={(event) => setForm((f) => ({ ...f, avatarUrl: event.target.value }))}
            placeholder="https://"
          />
        </label>

        <section className="account-profile-readonly">
          <h3>Roles</h3>
          <div className="admin-users-badges">
            {(profile?.roles || []).map((role) => (
              <StatusBadge key={role.roleCode} label={role.roleName || role.roleCode} tone="neutral" />
            ))}
          </div>
          <p className="muted">
            {profile?.permissionCount ?? profile?.permissions?.length ?? 0} permissions assigned
            through your roles.
          </p>
        </section>

        <button type="submit" className="report-submit-cta" disabled={isSaving}>
          {isSaving ? "Saving…" : "Save profile"}
        </button>
      </form>
    </>
  );
};

export default ProfilePage;
